import {useMemo} from "react";
import {useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";
import {formatParameterValue, formatPrice} from "@reva/shared/number-format";
import {normalizeSpf, SPF_PARAMS} from "@reva/shared/parameter-resolution";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectVisibleObjectParametersCurried} from "../../redux/objectParametersSelectors";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectSpecApiParamInputs} from "../../redux/specSlice";
import {PanelTitle, SectionTitle} from "./ObjectInputs";

/** Displays input parameters in a spec-formated, non-editable way.
 * @param {Record<string, any>} outputData data object to display
 * @param {boolean} fullsize true to display in full page, false for half-page
 * @param {AvmSpecSegment|null} [segmentOverride] Segment, in dem die Daten angezeigt werden, statt dem der Bewertung
 * @return {JSX.Element}
 */
export function ObjectOutputGrid({outputData, fullsize, segmentOverride = null}) {
    const {t, language} = useRevaTranslation();
    const valuationSegment = useSelector(selectSegment);
    const segment = segmentOverride ?? valuationSegment;
    const spec = useSelector(selectSpecApiParamInputs)?.[segment];
    const selectedColumns = useSelector(
        selectVisibleObjectParametersCurried("object/overview", "required", outputData, segmentOverride),
    );
    const visibleColumns = useMemo(
        () => onlyColumnsWithDisplayableRows(selectedColumns, outputData, spec),
        [selectedColumns, outputData, spec],
    );
    const size = !fullsize || visibleColumns.length === 1 ? 6 : 12 / visibleColumns.length;

    return (
        <div className="grid grid-cols-12 gap-4 text-color">
            {visibleColumns.map((column) => (
                <div key={column.key} className={`col-span-${size}`}>
                    <SectionTitle
                        title={selectByLanguage(language, column.titleDe, column.titleEn)}
                        firstSection={true}
                    />
                    {column.blocks.map((block) => (
                        <div
                            className="rounded-border bg-surface-100 dark:bg-surface-700 px-4 py-1 panelForInputs"
                            key={block.key}
                        >
                            <PanelTitle title={selectByLanguage(language, block.titleDe, block.titleEn)} />
                            {block.rows?.map((row) =>
                                showParameterRow(row, outputData, spec?.[row.parameter], language, t),
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

/**
 * Nur Zeilen mit darstellbarem Wert, danach ohne leere Blöcke und Spalten. Der Selektor lässt required-,
 * segment- und Konflikt-Zeilen auch ohne Wert durch; `showParameterRow` würde sie nicht rendern, der leere
 * Block samt Spaltentitel bliebe aber stehen (#19302 B10).
 * @param {RevaSpecObjectInputColumn[]} columns
 * @param {Record<string, any>} values
 * @param {Record<string, RevaSpecInputParameter>|undefined} spec Parameter-Spec des Segments
 * @return {RevaSpecObjectInputColumn[]}
 */
function onlyColumnsWithDisplayableRows(columns, values, spec) {
    const isDisplayable = (row) =>
        values?.[row.parameter] != null && (spec?.[row.parameter] != null || row.parameter === "segment");
    return columns
        .map((column) => ({
            ...column,
            blocks: column.blocks
                .map((block) => ({...block, rows: block.rows?.filter(isDisplayable) ?? []}))
                .filter((block) => block.rows.length > 0),
        }))
        .filter((column) => column.blocks.length > 0);
}

/**
 * @param {RevaSpecObjectInputRow} row
 * @param {Record<string, any>} values
 * @param {RevaSpecInputParameter} details
 * @param {RevaLanguage} language
 * @param {function} t
 * @return {JSX.Element}
 */
function showParameterRow(row, values, details, language, t) {
    const parameter = row.parameter;
    const rawValue = values[parameter];
    if (rawValue == null || (details == null && parameter !== "segment")) return null;

    const label = selectByLanguage(
        language,
        row.nameOverrideDe ?? details?.nameDe,
        row.nameOverrideEn ?? details?.nameEn,
    );
    if (parameter === "special_property_features_values")
        return showSpecialPropertyFeatures(parameter, values, language, t);

    const options = parameter === "ownership_share" ? {formatOverride: "DOUBLE(0-4)"} : undefined;
    const value =
        parameter === "segment"
            ? t(`object.segments.${rawValue}`)
            : formatParameterValue(language, details, rawValue, options);

    return (
        <div className={"flex justify-between my-2"} key={parameter}>
            <div>{label}</div>
            <div>{value}</div>
        </div>
    );
}

function showSpecialPropertyFeatures(parameter, values, language, t) {
    const spf = normalizeSpf(values);
    const spfValues = spf?.[SPF_PARAMS.values];
    const spfLabels = spf?.[SPF_PARAMS.names];
    const spfValuesLending = spf?.[SPF_PARAMS.valuesLending];
    const spfShares = spf?.[SPF_PARAMS.shares];
    const fmt = (val, share) =>
        val != null ? formatPrice(val, language) : share != null ? formatPrice(share, language, false, "%") : null;

    return (
        <div className={"my-2"} key={parameter}>
            <div>{t("object.tab_inspection.input.spf_title")}</div>
            <div className={"flex justify-between mt-2"}>
                <div>
                    {spfLabels?.map((label, i) => (
                        <div key={i} className="pl-6">
                            {label}
                        </div>
                    ))}
                </div>
                <div>
                    {spfLabels?.map((_, i) => {
                        const parts = [
                            ...new Set(
                                [
                                    fmt(spfValues?.[i], spfShares?.[i]),
                                    fmt(spfValuesLending?.[i], spfShares?.[i]),
                                ].filter((x) => x != null),
                            ),
                        ];
                        return <div key={i}>{parts.join(" / ")}</div>;
                    })}
                </div>
            </div>
        </div>
    );
}
